// Görbe (DESIGN.md "Diagramok" 1): a rate over time. A smooth line for what
// happened with a soft gradient under it, a dashed one for the plan, and an
// emphasised point on "now" — a ring and a halo. A faint grid, the values on
// the right, and a translucent popover on hover. It draws once, in
// --emc-draw-duration, and not at all for a reader who asked for less motion.
//
// Colours are tokens and nothing else. Actual and plan are told apart by
// solid versus dashed and by the legend, not by hue alone.

import { useId, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { useDrawDuration } from "@/components/emc/charts/motion";
import { logTicks } from "@/components/emc/charts/scale";
import { ChartPopover, CHART } from "@/components/emc/charts/theme";

export interface CurvePoint {
  /** The x-axis text: a day, an hour. */
  label: string;
  /** What happened; null for the future. */
  actual?: number | null;
  /** What was planned; null where there is no plan. */
  plan?: number | null;
}

interface Props {
  data: CurvePoint[];
  /** Index of "now"; defaults to the last point that has an actual value. */
  nowIndex?: number;
  formatValue?: (value: number) => string;
  actualLabel?: string;
  planLabel?: string;
  /** What the chart shows, for a screen reader. */
  ariaLabel: string;
  /** Size comes from the class; the default is h-60. */
  className?: string;
  /** "log" for a ramp that doubles (UI-6): every doubling is the same step,
   * so the first days stay visible beside the last and a day's lag reads as
   * the same gap whenever it happened. The axis runs decade to decade, and a
   * zero — which a logarithm cannot place — is left out of the line. */
  scale?: "linear" | "log";
  /** The series colour, a token; the first chart colour by default. */
  color?: string;
}

function defaultFormat(value: number): string {
  return value.toLocaleString("hu-HU");
}

function lastActual(data: CurvePoint[]): number {
  for (let index = data.length - 1; index >= 0; index -= 1) {
    const actual = data[index]?.actual;
    if (actual !== null && actual !== undefined) return index;
  }
  return -1;
}

interface TooltipEntry {
  dataKey?: unknown;
  value?: unknown;
}

function CurveTooltip({
  active,
  payload,
  label,
  formatValue,
  actualLabel,
  planLabel,
  color,
}: {
  active?: boolean;
  payload?: readonly TooltipEntry[];
  label?: unknown;
  formatValue: (value: number) => string;
  actualLabel: string;
  planLabel: string;
  color: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const valueOf = (key: string) => {
    const entry = payload.find((item) => item.dataKey === key);
    return typeof entry?.value === "number" ? entry.value : null;
  };
  const actual = valueOf("actual");
  const plan = valueOf("plan");
  return (
    <ChartPopover title={String(label)}>
      {actual !== null && (
        <p className="m-0 flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="size-2 rounded-flag" style={{ background: color }} />
            {actualLabel}
          </span>
          <span className="font-semibold tabular-nums">{formatValue(actual)}</span>
        </p>
      )}
      {plan !== null && (
        <p className="m-0 flex items-center justify-between gap-4 text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="w-2.5 border-t-[1.5px] border-dashed border-muted-foreground" />
            {planLabel}
          </span>
          <span className="tabular-nums">{formatValue(plan)}</span>
        </p>
      )}
    </ChartPopover>
  );
}

export function Curve({
  data: given,
  nowIndex,
  formatValue = defaultFormat,
  actualLabel = "tény",
  planLabel = "terv",
  ariaLabel,
  className,
  scale = "linear",
  color = CHART.series,
}: Props) {
  const duration = useDrawDuration();
  // "Egyszer": the first render draws, later data updates do not redraw.
  const [drawn, setDrawn] = useState(false);
  const animate = duration > 0 && !drawn;
  const titleId = useId();
  const gradientId = useId();

  const log = scale === "log";
  // On a logarithmic axis a zero has no place: it is left out, not faked.
  const data = log
    ? given.map((point) => ({
        ...point,
        actual: point.actual !== null && point.actual !== undefined && point.actual > 0 ? point.actual : null,
        plan: point.plan !== null && point.plan !== undefined && point.plan > 0 ? point.plan : null,
      }))
    : given;
  const ticks = log ? logTicks(data.flatMap((point) => [point.actual, point.plan])) : undefined;
  const floor = ticks?.[0];
  const domain: [number, number] | undefined = ticks
    ? [ticks[0] ?? 1, ticks[ticks.length - 1] ?? 10]
    : undefined;

  const now = nowIndex ?? lastActual(data);
  const nowPoint = now >= 0 ? data[now] : undefined;
  const hasPlan = data.some((point) => point.plan !== null && point.plan !== undefined);

  return (
    <figure className="m-0 flex flex-col gap-2" aria-labelledby={titleId}>
      <figcaption id={titleId} className="sr-only">
        {ariaLabel}
      </figcaption>
      {hasPlan && (
        <div className="flex items-center gap-4 text-label text-muted-foreground" aria-hidden="true">
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-flag" style={{ background: color }} />
            {actualLabel}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 border-t-[1.5px] border-dashed border-muted-foreground" />
            {planLabel}
          </span>
        </div>
      )}
      <div className={cn("h-60 w-full", className)}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 14, right: 4, bottom: 0, left: 4 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" style={{ stopColor: color, stopOpacity: 0.32 }} />
                <stop offset="1" style={{ stopColor: color, stopOpacity: 0.02 }} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={CHART.grid} />
            <XAxis
              dataKey="label"
              axisLine={{ stroke: CHART.axis }}
              tickLine={false}
              tick={CHART.tick}
              tickMargin={8}
              minTickGap={24}
            />
            <YAxis
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={CHART.tick}
              tickFormatter={(value: number) => formatValue(value)}
              width="auto"
              {...(log && ticks && domain
                ? {
                    scale: "log" as const,
                    domain,
                    ticks,
                    allowDataOverflow: true,
                  }
                : {})}
            />
            <Tooltip
              cursor={{ stroke: CHART.cursor }}
              isAnimationActive={false}
              content={(props) => (
                <CurveTooltip
                  active={props.active}
                  payload={props.payload as readonly TooltipEntry[] | undefined}
                  label={props.label}
                  formatValue={formatValue}
                  actualLabel={actualLabel}
                  planLabel={planLabel}
                  color={color}
                />
              )}
            />
            <Area
              type="monotone"
              dataKey="actual"
              stroke="none"
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              // On a log axis the area stands on the lowest decade, not on 0.
              {...(floor !== undefined ? { baseValue: floor } : {})}
              connectNulls={false}
              isAnimationActive={animate}
              animationDuration={duration}
              animationEasing="ease-out"
              activeDot={false}
            />
            {hasPlan && (
              <Line
                type="monotone"
                dataKey="plan"
                stroke={CHART.plan}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                strokeLinecap="round"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="actual"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
              activeDot={{ r: 4, fill: CHART.surface, stroke: color, strokeWidth: 2 }}
              connectNulls={false}
              isAnimationActive={animate}
              animationDuration={duration}
              animationEasing="ease-out"
              onAnimationEnd={() => setDrawn(true)}
            />
            {nowPoint && typeof nowPoint.actual === "number" && (
              <ReferenceDot
                x={nowPoint.label}
                y={nowPoint.actual}
                ifOverflow="visible"
                shape={(props: { cx?: number; cy?: number }) => (
                  <g>
                    <circle cx={props.cx} cy={props.cy} r={10} fill={color} fillOpacity={0.16} />
                    <circle cx={props.cx} cy={props.cy} r={4.8} fill={CHART.surface} stroke={color} strokeWidth={2.4} />
                  </g>
                )}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <span className="sr-only">
        {nowPoint && typeof nowPoint.actual === "number"
          ? `Most (${nowPoint.label}): ${formatValue(nowPoint.actual)}.`
          : ""}
      </span>
    </figure>
  );
}
